/**
 * Plausible Analytics Provider
 */

import type { AnalyticsProvider, AnyAnalyticsEvent, UserTraits } from '../types';

declare global {
  interface Window {
    plausible?: (eventName: string, options?: { 
      props?: Record<string, string | number | boolean>;
      callback?: () => void;
    }) => void;
  }
}

export class PlausibleProvider implements AnalyticsProvider {
  name = 'Plausible Analytics';
  enabled: boolean;
  private debug: boolean;

  constructor(domain: string, debug: boolean = false) {
    this.debug = debug;
    this.enabled = this.isAvailable();
    
    if (this.debug) {
      console.log(`[Plausible] Initialized for domain: ${domain}`);
    }
  }

  private isAvailable(): boolean {
    return typeof window !== 'undefined' && typeof window.plausible === 'function';
  }

  async track(event: AnyAnalyticsEvent): Promise<void> {
    if (!this.enabled) return;

    const eventName = this.formatEventName(event);
    const props = this.mapEventProperties(event);

    if (this.debug) {
      console.log('[Plausible] Track:', eventName, props);
    }

    window.plausible!(eventName, { props });
  }

  async identify(_userId: string, _traits?: UserTraits): Promise<void> {
    // Plausible doesn't support user identification
    // This is actually good for privacy!
    if (this.debug) {
      console.log('[Plausible] Identify called but not supported - privacy by design');
    }
  }

  async page(name: string, properties?: Record<string, any>): Promise<void> {
    if (!this.enabled) return;

    // Plausible automatically tracks pageviews
    // But we can track custom pageview events if needed
    if (properties?.track_manually) {
      const props = this.sanitizeProperties(properties);
      
      if (this.debug) {
        console.log('[Plausible] Manual pageview:', name, props);
      }

      window.plausible!('pageview', { props });
    }
  }

  async reset(): Promise<void> {
    // Plausible doesn't track users, so nothing to reset
    if (this.debug) {
      console.log('[Plausible] Reset called but not needed - no user tracking');
    }
  }

  private formatEventName(event: AnyAnalyticsEvent): string {
    // Plausible prefers readable event names
    const category = event.category.charAt(0).toUpperCase() + event.category.slice(1);
    const action = event.event
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
    
    return `${category}: ${action}`;
  }

  private mapEventProperties(event: AnyAnalyticsEvent): Record<string, string | number> {
    const props: Record<string, string | number> = {};
    
    // Plausible only supports string and number props
    if (event.properties) {
      for (const [key, value] of Object.entries(event.properties)) {
        if (value === null || value === undefined) continue;
        
        // Convert property names to readable format
        const propName = key
          .split('_')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ');
        
        if (typeof value === 'string' || typeof value === 'number') {
          props[propName] = value;
        } else if (typeof value === 'boolean') {
          props[propName] = value ? 'Yes' : 'No';
        } else if (Array.isArray(value)) {
          props[propName] = value.join(', ');
        } else {
          // For objects, use a simplified representation
          props[propName] = String(value);
        }
      }
    }
    
    // Add event metadata
    props['Event Type'] = event.event;
    
    return props;
  }

  private sanitizeProperties(properties: Record<string, any>): Record<string, string | number> {
    const sanitized: Record<string, string | number> = {};
    
    for (const [key, value] of Object.entries(properties)) {
      if (value === null || value === undefined) continue;
      
      // Plausible has more relaxed property name rules
      const sanitizedKey = key.substring(0, 50);
      
      if (typeof value === 'string') {
        sanitized[sanitizedKey] = value.substring(0, 200);
      } else if (typeof value === 'number') {
        sanitized[sanitizedKey] = value;
      } else if (typeof value === 'boolean') {
        sanitized[sanitizedKey] = value ? 'true' : 'false';
      } else {
        sanitized[sanitizedKey] = String(value).substring(0, 200);
      }
    }
    
    return sanitized;
  }
}